<template>
  <div>
    <FilterPanel
      v-if="!isShared"
      :filter-text="filterText"
      :owners-list="ownersList"
      :selected-owner="selectedOwner"
      :has-external-filters="hasExternalFilters"
      :fixed-at="contentTop"
      @onFilterResults="filterResults"
    >
      <template #top>
        <div class="row">
          <div class="element-row">
            <p>Keywords Criteria</p>
          </div>
        </div>
        <div class="row">
          <Select
            v-model="filterCriteria"
            class="select-input left"
            @on-change="updateFilterCriteria"
          >
            <Option
              value="all"
              label="All"
            />
            <Option
              value="table"
              label="Table Name"
            />
            <Option
              value="field"
              label="Field Name"
            />
            <Option
              value="owner"
              label="Owner"
            />
          </Select>
          <Select
            v-model="filterMode"
            class="select-input right"
            @on-change="updateFilterMode"
          >
            <Option
              value="abc"
              label="abc"
            />
            <Option
              value="regex"
              label=".*"
            />
          </Select>
        </div>

        <div class="row">
          <div class="element-row">
            <p>By Database</p>
          </div>
        </div>
        <div class="row">
          <Select
            v-model="selectedDatabase"
            clearable
            placeholder="Select Database"
            class="select-input"
            @on-change="updateDatabase"
          >
            <Option
              v-for="database in databasesList"
              :key="database.name"
              :label="database.name"
              :value="database.name"
            />
          </Select>
        </div>

        <div class="row">
          <div class="element-row">
            <p>By Schema</p>
          </div>
        </div>
        <div class="row">
          <Select
            v-model="selectedSchema"
            clearable
            placeholder="Select Schema"
            :disabled="!selectedDatabase"
            class="select-input"
            @on-change="updateSchema"
          >
            <Option
              v-for="schema in (databasesList[databaseIndex] || []).schemas"
              :key="schema.name"
              :label="schema.name"
              :value="schema.name"
            />
          </Select>
        </div>
      </template>
    </FilterPanel>

    <HintBox
      :show-hint-box="showHintBox"
      :hints="hints"
      show-icon-big
      color-icon
      @closeHint="showHintBox = false"
      @updateHintPreference="updateHintPreference"
    />

    <div class="refresh-container">
      <Tooltip
        content="Refresh"
        style="margin-right: 8px;"
      >
        <Icon
          size="24"
          type="md-refresh"
          class="refresh-icon"
          @click="refreshTableMetadata"
        />
      </Tooltip>
      <div class="last-updated-text">
        <b>Last Pulled from Snowflake:</b> {{ lastUpdatedTime }}
      </div>
    </div>
    <Tabs
      v-model="selectedTab"
      type="card"
      :animated="false"
      style="margin-top: 8px"
    >
      <TabPane
        label="Table"
        name="table"
      >
        <div v-if="loading">
          <Card>
            <div style="display: flex; align-items: center">
              <h3>
                <span>
                  Table Name (Source: Snowflake &#8594; DATABASE.SCHEMA)
                </span>
              </h3>
              <span style="margin-left: 8px">
                <i class="far fa-file-alt action-icon" />
                <i class="fas fa-share-alt action-icon" />
              </span>
            </div>
            <div style="margin: 8px 0 16px">
              <div style="margin-bottom: 16px">
                <div><b>Annotation Owner:</b> Some Body</div>
                <div><b>Kind: </b>Table</div>
                <div><b>Owner: </b>Some Body</div>
                <div><b>Number of rows: </b>1000</div>
                <div><b>Bytes: </b>1000</div>
                <div><b>Created on: </b>Jan 01, 2000 at 12:00 pm</div>
              </div>
              <div>Table Description</div>
              <Table
                class="data-catalog-table"
                :row-class-name="rowClassName"
                :columns="tableViewColumns"
                :data="loadingTableViewData"
                border
                max-height="500"
              >
                <template
                  slot="key"
                  slot-scope="{ row }"
                >
                  {{ row.key }}
                </template>
                <template
                  slot="description"
                  slot-scope="{ row }"
                >
                  {{ row.description }}
                </template>
              </Table>
              <Spin
                fix
                size="large"
              />
            </div>
          </Card>
        </div>
        <div
          v-else-if="filteredTableMetadata.length <= 0"
          class="no-data-tables-msg"
        >
          No Data Tables to Display
        </div>
        <div v-else>
          <Card
            v-for="(table, index) in tableViewDataForPage"
            :key="index"
            style="margin-bottom:16px"
          >
            <div style="display: flex; align-items: center">
              <h3>
                <span v-if="table.schema">
                  {{ table.name }} (Source: {{ table.source }} &#8594; {{ table.database }}.{{ table.schema }})
                </span>
                <span v-else>
                  {{ table.name }} (Source: {{ table.source }})
                </span>
              </h3>
              <span style="margin-left: 8px">
                <Tooltip content="Find in Golden Query">
                  <i
                    class="far fa-file-alt action-icon"
                    @click="findInGoldenQuery(table.name)"
                  />
                </Tooltip>
                <Tooltip content="Share Table">
                  <i
                    class="fas fa-share-alt action-icon"
                    @click="shareLink(table)"
                  />
                </Tooltip>
                <Tooltip content="Preview Table Data">
                  <i
                    class="fas fa-database action-icon"
                    @click="previewTableData(table)"
                  />
                </Tooltip>
                <Tooltip
                  v-if="isTableLDW(table) || isTableProdData(table)"
                  content="See usage within DAG"
                >
                  <i
                    class="fas fa-code action-icon"
                    @click="showDagMapping(table)"
                  />
                </Tooltip>
              </span>
            </div>
            <div style="margin: 8px 0 16px">
              <div style="margin-bottom: 16px">
                <div v-if="table.database.toLowerCase() !== 'ldw'">
                  <b>Annotation Owner:</b>
                  <span>{{ table.annotationOwner || 'No Annotation Owner Provided' }}</span>
                </div>
                <div v-if="table.kind">
                  <b>Kind: </b>{{ table.kind }}
                </div>
                <div v-if="table.owner">
                  <b>Owner: </b>{{ table.owner }}
                </div>
                <div v-if="table.numRows">
                  <b>Number of rows: </b>{{ formatNumber(table.numRows) }}
                </div>
                <div v-if="table.bytes">
                  <b>Bytes: </b>{{ formatNumber(table.bytes) }}
                </div>
                <div v-if="table.createdOn">
                  <b>Created on: </b>{{ `${$moment(new Date(table.createdOn)).format('MMM DD, YYYY')} at ${$moment(new Date(table.createdOn)).format('hh:mm A')}` }}
                </div>
              </div>
              <div v-if="table.description">
                {{ table.description }}
              </div>
            </div>
            <Table
              v-if="table.tableData"
              :ref="`dataTable${index}`"
              class="data-catalog-table"
              :row-class-name="rowClassName"
              :columns="table.tableColumns"
              :data="table.tableData"
              border
              max-height="500"
            >
              <template
                slot="key"
                slot-scope="{ row }"
              >
                <div v-if="row.is_primary_raw">
                  PRIMARY_KEY
                </div>
                <div v-else-if="row.key">
                  <div
                    v-if="isForeignKey(row.key)"
                    @click="showTableDetailModal(row.key.reference)"
                  >
                    <Tooltip
                      :content="row.key.reference || 'No Reference Provided'"
                      max-width="350"
                      style="cursor: pointer"
                    >
                      <div>
                        {{ row.key.name }}
                      </div>
                    </Tooltip>
                  </div>
                  <div v-else>
                    {{ row.key.name }}
                  </div>
                </div>
              </template>
              <template
                slot="description"
                slot-scope="{ row }"
              >
                <div style="margin-top:16px;margin-bottom:16px">
                  <div v-if="row.description">
                    {{ row.description }}
                  </div>
                </div>
              </template>
            </Table>
          </Card>
          <Page
            v-if="!isShared"
            :current="currentPage + 1"
            :page-size="5"
            :total="filteredTableMetadata.length"
            show-elevator
            @on-change="onPageChange"
          />
        </div>
      </TabPane>
      <TabPane
        label="ER Diagram"
        name="erDiagram"
      >
        <ERDiagram
          v-if="selectedTab === 'erDiagram'"
          :tables="ERDiagramTableMetadata"
          :filtered-tables="filteredTableMetadata"
          :search-text="filterText"
        />
      </TabPane>
    </Tabs>

    <TableDetailModal
      v-model="tableDetailModalData.visible"
      :tables="tableMetadata ? tableMetadata.tables : []"
      :table="tableDetailModalData.table"
    />

    <QueryTableModal
      v-model="queryTableModalData.visible"
      :title="queryTableModalData.title"
      :data="queryTableModalData.data"
      :loading="queryTableModalData.loading"
    />

    <Modal
      v-model="dagMappingModal.visible"
      :width="1000"
    >
      <div slot="header">
        <div
          v-if="dagMappingModal.table"
          style="font-size: 16px"
        >
          DAG Files for <b>{{ `${dagMappingModal.table.database}.${dagMappingModal.table.schema}.${dagMappingModal.table.name}` }}</b>
        </div>
      </div>
      <template v-if="dagMappingModal.loading">
        <Spin
          fix
          size="large"
        />
      </template>
      <template v-else-if="!dagMappingModal.table">
        <div>
          No table was specified
        </div>
      </template>
      <template v-else-if="!dagMappingModal.dagFiles.length || !selectedDagFile">
        <div>
          This table does not appear to be used in any DAG files
        </div>
      </template>
      <template v-else>
        <div>
          <div style="display: flex">
            <Select
              v-model="dagMappingModal.selectedDagFilePath"
              style="margin-bottom: 16px; margin-right: 16px"
            >
              <Option
                v-for="(dagFilePath, index) in dagFilePaths"
                :key="index"
                :label="dagFilePath"
                :value="dagFilePath"
              />
            </Select>
            <div style="display: flex; white-space: nowrap; margin-top: 8px">
              <b style="margin-right: 8px">Dark Mode:</b>
              <i-switch
                :value="dagCmOptions.theme !== 'default'"
                @on-change="onDarkModeSwitch"
              />
            </div>
          </div>
          <codemirror
            ref="dag-cm"
            :value="selectedDagFile.content"
            :options="dagCmOptions"
            class="dag-codemirror"
            @ready="highlightTableNameInDagFile"
            @changes="highlightTableNameInDagFile"
          />
        </div>
      </template>
      <div slot="footer">
        <Button
          type="default"
          @click="onDagMappingModalClose"
        >
          Close
        </Button>
      </div>
    </Modal>
  </div>
</template>
<script>
import ERDiagram from './helper-components/ERDiagram'
import FilterPanel from './helper-components/FilterPanel'
import HintBox from './helper-components/HintBox'
import TableDetailModal from './helper-components/TableDetailModal'
import QueryTableModal from './helper-components/QueryTableModal'
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment-timezone'
import { fuzzySearch, fuzzyScore } from '@/helpers/fuzzy-search'
import { mapState } from 'vuex'

import 'codemirror/theme/mbo.css'

export default {
  components: {
    ERDiagram,
    FilterPanel,
    HintBox,
    TableDetailModal,
    QueryTableModal
  },

  props: {
    contentTop: Number
  },

  data () {
    const dagCmDarkMode = Boolean(this.$root.preferences.dataCatalog.dagCmDarkMode)

    return {
      user: null,
      tableMetadata: null,
      filteredTableMetadata: [],
      tableViewColumns: [
        {
          title: 'Field Name',
          key: 'name',
          maxWidth: 200
        },
        {
          title: 'Data Type',
          key: 'type',
          maxWidth: 200
        },
        {
          title: 'Key',
          slot: 'key',
          maxWidth: 200
        },
        {
          title: 'Description',
          slot: 'description'
        }
      ],
      loadingTableViewData: [
        {
          name: 'Field 1',
          type: 'Int',
          key: 'Primary Key',
          description: 'This is the id for the table'
        },
        {
          name: 'Field 2',
          type: 'Int',
          key: '',
          description: 'This is the second field'
        },
        {
          name: 'Field 3',
          type: 'String',
          key: '',
          description: 'This is the third field'
        },
        {
          name: 'Field 4',
          type: 'Int',
          key: '',
          description: 'This is the fourth field'
        },
        {
          name: 'Field 5',
          type: 'Int',
          key: '',
          description: 'This is the fifth field'
        }
      ],
      currentPage: 0,
      filterText: this.$root.preferences.dataCatalog.filterText || '',
      sourceFilter: [
        'Limebikeweb',
        'Snowflake'
      ],
      loading: true,
      editMode: {},
      viewMode: {},
      tableChangeLog: {},
      fieldChangeLog: {},
      lastUpdatedTime: 'N/A',
      toggleConditionAndLabel: 'AND',
      toggleConditionOrLabel: 'OR',
      isShared: false,
      selectedTab: 'table',
      tablesOwners: [],
      ownersList: [],
      selectedOwner: this.$root.preferences.dataCatalog.selectedOwner || '',
      tablesDatabases: [],
      filterCriteria: this.$root.preferences.dataCatalog.filterCriteria || 'all',
      filterMode: this.$root.preferences.dataCatalog.filterMode || 'abc',
      databasesList: [],
      selectedDatabase: this.$root.preferences.dataCatalog.selectedDatabase || '',
      selectedSchema: this.$root.preferences.dataCatalog.selectedSchema || '',
      dbCache: null,
      databaseIndex: 0,
      hasExternalFilters: !_.isEmpty(this.$root.preferences.dataCatalog.selectedDatabase) ||
          !_.isEmpty(this.$root.preferences.dataCatalog.selectedSchema),
      showHintBox: false,
      hintsPreferences: {},
      hints: [
        {
          id: 1,
          title: 'Hint',
          type: 'info',
          description: `Do you want to filter by <b>Keywords, Database, Schema</b> or <b>Owner</b>? Just click on the filter icon <i class="fas fa-filter" />.`,
          hidden: false
        }
      ],
      tableDetailModalData: {
        visible: false,
        table: null
      },
      dagMappingModal: {
        visible: false,
        loading: false,
        dagFiles: [],
        table: null,
        selectedDagFilePath: ''
      },
      dagCmOptions: {
        readOnly: true,
        mode: { name: 'text/plain' },
        theme: dagCmDarkMode ? 'mbo' : 'default',
        lineNumbers: true
      },
      markedTexts: [],
      fieldFuzzyThreshold: 0.2,
      queryTableModalData: {
        visible: false,
        data: [],
        title: '',
        loading: true
      }
    }
  },

  computed: {
    tableViewData () {
      return Object.freeze(this.filteredTableMetadata.map(table => {
        const userChanges = table.userChanges || []

        const viewData = {
          ...table,
          tableColumns: this.tableViewColumns,
          tableData: table.columns
        }

        viewData.userChanges = userChanges

        return viewData
      }))
    },

    tableViewDataForPage () {
      const startingIndex = this.currentPage * 5
      return Object.freeze(this.tableViewData.slice(startingIndex, startingIndex + 5))
    },

    ERDiagramTableMetadata () {
      const rawTables = this.tableMetadata ? this.tableMetadata.tables : []
      const tables = rawTables.filter(table => table.database.toLowerCase() === 'ldw')
      const filteredTables = this.filteredTableMetadata.filter(table => table.database.toLowerCase() === 'ldw')
      const tableHash = {}
      const targetTableHash = {}

      // Get tables that have foreign key relations with filtered tables
      filteredTables.forEach(table => {
        const tableName = `${table.database}.${table.schema}.${table.name}`.toLowerCase()
        const foreignKeyCols = this.getForeignKeyColumns(table)

        tableHash[tableName] = true

        foreignKeyCols.forEach(foreignKeyCol => {
          const sourceTableName = foreignKeyCol.foreignKey.sourceTable.toLowerCase()

          targetTableHash[sourceTableName] = true
        })
      })

      const targetTables = tables.filter(table => {
        const tableName = `${table.database}.${table.schema}.${table.name}`.toLowerCase()

        return !tableHash[tableName] && !!targetTableHash[tableName]
      })

      // Tables that have foreign key relations with filtered metadata tables
      const pointingTables = tables.filter(table => {
        const tableName = `${table.database}.${table.schema}.${table.name}`.toLowerCase()
        let hasForeignKeyRelation = false

        if (!tableHash[tableName] && !targetTableHash[tableName]) {
          const foreignKeyCols = this.getForeignKeyColumns(table)

          foreignKeyCols.forEach(foreignKeyCol => {
            const target = foreignKeyCol.foreignKey.sourceTable.toLowerCase()

            if (tableHash[target]) {
              hasForeignKeyRelation = true
            }
          })
        }

        return hasForeignKeyRelation
      })

      return [...filteredTables, ...targetTables, ...pointingTables]
    },

    dagFilePaths () {
      return this.dagMappingModal.dagFiles.map(dagFile => dagFile.path)
    },

    selectedDagFile () {
      const filePath = this.dagMappingModal.selectedDagFilePath.toLowerCase()

      return this.dagMappingModal.dagFiles.find(dagFile => dagFile.path.toLowerCase() === filePath)
    },

    ...mapState('snowflake', {
      snowflakeData: state => state.snowflakeData,
      snowflakeLastUpdated: (state, getters) => getters.snowflakeLastUpdated
    })
  },

  watch: {
    $route (newRoute) {
      if (newRoute.path.includes('/data-catalog')) {
        this.setFilterBasedOnRouteParams()
        this.createTableMetadata()
      }
    },

    'dagMappingModal.selectedDagFilePath' (dagFilePath) {
      if (dagFilePath.includes('.')) {
        const fileExtension = dagFilePath.split('.').pop()
        let modeName = ''
        switch (fileExtension) {
          case 'py':
            modeName = 'text/x-python'
            break
          case 'sql':
            modeName = 'text/x-sql'
            break
          default:
            modeName = 'text/plain'
        }

        this.$set(this.dagCmOptions, 'mode', { name: modeName })
      }
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.hasOwnProperty('dataCatalog')) {
      this.$set(this.$root.preferences, 'dataCatalog', {})
    }

    sessionStorage.setItem('context', 'data_catalog')
  },

  created () {
    this.setFilterBasedOnRouteParams()
  },

  mounted () {
    this.user = this.$auth.getUser()
    this.getSnowflakeLastUpdated()
    this.createTableMetadata()

    let previousHintValue = false
    if (this.$root.preferences.dataCatalog.hasOwnProperty('blockHint')) {
      previousHintValue = this.$root.preferences.dataCatalog.blockHint
      delete this.$root.preferences.dataCatalog.blockHint
    }

    this.hintsPreferences = this.$root.preferences.dataCatalog.hints
      ? JSON.parse(this.$root.preferences.dataCatalog.hints)
      : {}
    this.hints.forEach(hint => {
      if (!this.hintsPreferences[hint.id]) {
        const isHidden = hint.id === 1 && previousHintValue
        this.hintsPreferences[hint.id] = isHidden
      }
      this.$set(hint, 'hidden', this.hintsPreferences[hint.id])
    })
    this.$set(this.$root.preferences.dataCatalog, 'hints', JSON.stringify(this.hintsPreferences))
    this.showHintBox = this.hints.some(hint => !hint.hidden)
  },

  updated () {
    this.$nextTick(() => {
      this.scrollToHighlightedRow()
    })
  },

  methods: {
    /**
     * Changes filter options based on route parameters
     */
    setFilterBasedOnRouteParams () {
      const { databaseName, schemaName, tableName } = this.$route.params

      if (databaseName) {
        this.selectedDatabase = databaseName.toUpperCase()
      }

      if (schemaName) {
        this.selectedSchema = schemaName.toUpperCase()
      }

      if (tableName) {
        this.filterCriteria = 'table'
        this.filterMode = 'regex'
        this.filterText = `^${tableName}$`
      }
    },

    /**
     * Fetches the last updated datetime of the Snowflake metadata and stores it into
     * "lastUpdatedTime" var.
     */
    async getSnowflakeLastUpdated () {
      const timeZone = 'America/Los_Angeles'
      const jsonData = (await axios.get(`${process.env.VUE_APP_API_URI}/admin-console/last-updated-date`, {
        params: {
          data: 'snowflake'
        }
      })).data
      this.lastUpdatedTime = moment(jsonData.lastUpdated).tz(timeZone).format('MMM-DD-YYYY hh:mm A z')
    },

    /**
     * Retrieves the cached table metadata
     * @return {Promise}
     */
    getCachedTableMetadata () {
      return new Promise((resolve, reject) => {
        const dbRequest = indexedDB.open('DataCatalogDB', 1)

        dbRequest.onupgradeneeded = e => {
          const db = e.target.result
          db.createObjectStore('tableMetadata', { keyPath: 'id' })
        }

        dbRequest.onsuccess = e => {
          this.dbCache = e.target.result
          const readTransaction = this.dbCache.transaction('tableMetadata', 'readonly')
          const objectStore = readTransaction.objectStore('tableMetadata')
          const readRequest = objectStore.getAll()

          readRequest.onerror = e => {
            console.error(`Error while reading cache for Data Catalog: ${e}`)
            reject(e)
          }

          readRequest.onsuccess = e => {
            const result = e.target.result

            if (result.length > 0) {
              resolve(result[0])
            } else {
              resolve(null)
            }
          }
        }
        dbRequest.onerror = e => {
          this.dbCache = null
          console.error(`Error while performing operations on cache for Data Catalog: ${e}`)
          reject(e)
        }
      })
    },

    /**
     * Updates the cached table metadata
     * @param {Object} tableMetadata
     */
    updateCachedTableMetadata (tableMetadata) {
      if (this.dbCache) {
        const writeTransaction = this.dbCache.transaction('tableMetadata', 'readwrite')
        const objectStore = writeTransaction.objectStore('tableMetadata')
        const clearRequest = objectStore.clear()

        clearRequest.onsuccess = () => {
          objectStore.put({ ...tableMetadata, id: 1 })
        }
      }
    },

    /**
     * Sets up the table metadata needed for the Data Catalog.
     * First checks to see if snowflake data in Vuex has been initialized.
     * If not, it will check the cached table metadata. If cached data is not
     * present or stale, we will need to dispatch an action to initialize snowflake
     * data in Vuex.
     */
    async createTableMetadata () {
      this.loading = true
      if (_.isEmpty(this.snowflakeData)) {
        // Fetch cached table metadata first
        const cachedTableMetadata = await this.getCachedTableMetadata()

        if (cachedTableMetadata) {
          const lastUpdatedTime = cachedTableMetadata.lastUpdatedTime || new Date(0)
          const lastUpdatedMoment = moment(lastUpdatedTime, 'MMM-DD-YYYY hh:mm A z')
          const now = moment()
          const diff = moment.duration(now.diff(lastUpdatedMoment))

          if (diff.days() >= 1 || diff.months() >= 1 || diff.years() >= 1) {
            // Need to invalidate cache
            await this.refreshTableMetadata()
          } else {
            this.setTableMetadata(cachedTableMetadata)
          }
        } else {
          await this.refreshTableMetadata()
        }
      } else {
        this.setTableMetadata({
          ...this.snowflakeData,
          tables: this.snowflakeData.tables.map(table => ({
            ...table,
            database: table.databaseName,
            schema: table.schemaName
          }))
        })
      }
      this.loading = false
    },

    /**
     * Refreshes the table metadata.
     * @return {Promise}
     */
    refreshTableMetadata () {
      return new Promise(async () => {
        this.loading = true
        await this.$store.dispatch('snowflake/fetchSnowflakeData')
        // Need to modify input since snowflakeData properties do not match
        // cached table metadata's property name. For example, cached metadata
        // has property "database", while snowflakeData has its property called
        // "databaseName"
        this.setTableMetadata({
          ...this.snowflakeData,
          tables: this.snowflakeData.tables.map(table => ({
            ...table,
            database: table.databaseName,
            schema: table.schemaName
          }))
        })
        this.loading = false
      })
    },

    /**
     * Creates the Database, Schema and Owners lists for filtering.
     * @param {Array} tables
     */
    createFilterMetadata (tables) {
      tables.forEach(table => {
        const tableOwners = table.owner.split('; ') || []
        tableOwners.forEach(owner => {
          if (!this.tablesOwners[owner]) {
            this.tablesOwners[owner] = true
          }
        })

        if (!this.tablesDatabases[table.database]) {
          this.tablesDatabases[table.database] = []
        }

        if (!this.tablesDatabases[table.database][table.schema]) {
          this.tablesDatabases[table.database][table.schema] = true
        }
      })

      this.ownersList = Object.keys(this.tablesOwners).sort().map(owner => ({
        name: owner,
        selected: owner === this.selectedOwner
      }))

      this.databasesList = Object.keys(this.tablesDatabases).sort().map(database => ({
        name: database,
        selected: database === this.selectedDatabase,
        schemas: Object.keys(this.tablesDatabases[database]).sort().map(schema => ({
          name: schema,
          selected: schema === this.selectedSchema
        }))
      }))
    },

    /**
     * Sets the table metadata object used for data catalog
     * @param {Object} tableMetadata
     */
    setTableMetadata (tableMetadata) {
      this.tableMetadata = {
        ...tableMetadata,
        tables: tableMetadata.tables.map(table => {
          const { name, database, schema, kind, owner, numRows, bytes, createdOn, description, columns } = table
          const tableData = {
            name,
            kind,
            schema,
            database,
            owner,
            numRows,
            bytes,
            createdOn,
            description,
            columns: columns.map(col => ({
              ...col,
              key: this.createKeyObject(col)
            }))
          }

          return tableData
        })
      }
      this.createFilterMetadata(this.tableMetadata.tables)
      this.updateCachedTableMetadata(tableMetadata)
      this.filterResults()
    },

    /**
     * Returns true if the table is from LDW.
     * @param {Object} table - Table object
     * @return {Boolean} - true if it is LDW, false if not
     */
    isTableLDW (table) {
      return table.database.toLowerCase() === 'ldw'
    },

    /**
     * Returns true if the table if from production_data
     * @param {Object} table - Table object
     * @return {Boolean} = true if it is production_data, false if not
     */
    isTableProdData (table) {
      return table.database.toLowerCase() === 'production_data'
    },

    /**
     * Changes the results view to the selected page number.
     * @param {Number} pageNumber
     */
    onPageChange (pageNumber) {
      this.currentPage = pageNumber - 1
    },

    /**
     * Sets the filters for the queries list.
     * @param {Object} filterParams
     */
    filterResults (filterParams) {
      this.currentPage = 0
      if (!_.has(this.tableMetadata, 'tables') || !this.tableMetadata.tables.length) {
        return []
      }

      const {
        filterText = this.filterText,
        filterCriteria = this.filterCriteria,
        filterMode = this.filterMode,
        selectedDatabase = this.selectedDatabase,
        selectedSchema = this.selectedSchema,
        selectedOwner = this.selectedOwner,
        resetFilters
      } = filterParams || {}

      this.filterText = filterText
      this.filterCriteria = resetFilters ? 'all' : filterCriteria
      this.filterMode = resetFilters ? 'abc' : filterMode
      this.selectedDatabase = resetFilters ? '' : selectedDatabase
      this.selectedSchema = resetFilters ? '' : selectedSchema
      this.selectedOwner = selectedOwner

      this.$set(this.$root.preferences.dataCatalog, 'filterText', this.filterText)
      this.$set(this.$root.preferences.dataCatalog, 'filterCriteria', this.filterCriteria)
      this.$set(this.$root.preferences.dataCatalog, 'filterMode', this.filterMode)
      this.$set(this.$root.preferences.dataCatalog, 'selectedDatabase', this.selectedDatabase)
      this.$set(this.$root.preferences.dataCatalog, 'selectedSchema', this.selectedSchema)
      this.$set(this.$root.preferences.dataCatalog, 'selectedOwner', this.selectedOwner)

      const noFilter = !this.filterText && !this.selectedDatabase && !this.selectedOwner

      if (this.isShared || noFilter) {
        this.filteredTableMetadata = Object.freeze(this.tableMetadata ? this.tableMetadata.tables : [])
        return
      }

      let filteredTables = this.tableMetadata.tables

      if (this.filterText) {
        if (this.filterMode === 'abc') {
          filteredTables = this.fuzzyTableSearch(this.tableMetadata.tables)
        } else if (this.filterMode === 'regex') {
          filteredTables = this.tableMetadata.tables.filter(tableInfo => (
            this.tableNameMatch(tableInfo.name, tableInfo.schema) ||
            this.columnMatch(tableInfo.columns) ||
            this.ownerMatch(tableInfo.owner || '') ||
            this.ownerMatch(tableInfo.annotationOwner || '')
          ))
        }
      }

      this.filteredTableMetadata = Object.freeze(filteredTables.filter(tableInfo => {
        return (
          (!this.selectedDatabase || tableInfo.database === this.selectedDatabase) &&
          (!this.selectedSchema || tableInfo.schema === this.selectedSchema) &&
          (!this.selectedOwner || tableInfo.owner.includes(this.selectedOwner))
        )
      }))
    },

    /**
     * Performs fuzzy search on the table metadata
     * @param {Array} tables - The array of tables to be filtered
     * @return {Array} The filtered array of tables after fuzzy search is performed
     */
    fuzzyTableSearch (tables, options = {}) {
      let keys = []
      let threshold = 0.5

      switch (this.filterCriteria) {
        case 'table':
          keys = ['name']
          break
        case 'field':
          // Field search is trickier since each table has different number of fields. The calculated
          // fuzzy score is more messed up and causes the results to contain many false positives. In addition,
          // matched fields are highlighted in the UI when they are matched, so we don't want too
          // many fields to be highlighted during a search. As a result, we are setting the threshold
          // to be more strict.
          keys = ['columns.name']
          threshold = this.fieldFuzzyThreshold
          break
        case 'owner':
          keys = ['owner']
          break
        default:
          keys = [{
            name: 'name',
            weight: 0.9
          }, {
            name: 'columns.name',
            weight: 0.075
          }, {
            name: 'owner',
            weight: 0.025
          }]
      }

      return fuzzySearch(tables, this.filterText, keys, { threshold, ...options })
    },

    /**
     * Validates if the table name matches with the "filter text".
     * NOTE: Only applies if the filter criteria for keywords is 'ALL' or 'TABLE'.
     * @param {String} tableName
     * @param {String} schemaName
     * @return {Boolean}
     */
    tableNameMatch (tableName, schemaName) {
      if (this.filterCriteria !== 'all' && this.filterCriteria !== 'table') {
        return false
      }

      return this.nameMatch(tableName) || this.schemaNameMatch(tableName, schemaName)
    },

    /**
     * Validates if the table name matches with the "filter mode".
     * @param {String} tableName
     * @param {String} schemaName
     * @return {Boolean}
     */
    schemaNameMatch (tableName, schemaName) {
      const fullTableName = schemaName ? `${schemaName}.${tableName}` : tableName
      const regexStr = this.filterMode === 'regex' ? this.filterText : this.escapeRegex(this.filterText)
      const regex = new RegExp(regexStr, 'i')

      return fullTableName.match(regex)
    },

    /**
     * Validates if any column matches with the "filter text".
     * NOTE: Only applies if the filter criteria for keywords is 'ALL' or 'FIELD'.
     * @param {Array} columns
     * @return {Boolean}
     */
    columnMatch (columns) {
      if (this.filterCriteria !== 'all' && this.filterCriteria !== 'field') {
        return false
      }

      return columns.some(column => (
        this.nameMatch(column.name)
      ))
    },

    /**
     * Validates if any owner matches with the "filter text".
     * NOTE: Only applies if the filter criteria for keywords is 'ALL' or 'OWNER'.
     * @param {String} ownerName
     * @return {Boolean}
     */
    ownerMatch (ownerName) {
      if (this.filterCriteria !== 'all' && this.filterCriteria !== 'owner') {
        return false
      }

      return this.nameMatch(ownerName)
    },

    /**
     * Validates if the string "name" is a regex match for the filter text.
     * @param {String} name
     * @return {Boolean}
     */
    nameMatch (name) {
      const regex = new RegExp(this.filterText, 'i')

      return name.match(regex)
    },

    /**
     * Highlights a row in a table if the filter criteria is "ALL" or "FIELD" and if a column matches with
     * the "filter text".
     * @param {Object} row
     * @return {String}
     */
    rowClassName (row) {
      if (
        (this.filterCriteria === 'all' || this.filterCriteria === 'field') && this.filterText &&
        ((this.filterMode === 'abc' && fuzzyScore(row.name, this.filterText) <= this.fieldFuzzyThreshold) ||
        (this.filterMode === 'regex' && this.nameMatch(row.name)))
      ) {
        return 'highlighted-row'
      }

      return ''
    },

    /**
     * Scrolls each table to the first highlighted row.
     */
    scrollToHighlightedRow () {
      if (!this.filterText) {
        return
      }

      const numTables = this.tableViewDataForPage.length

      for (var i = 0; i < numTables; i++) {
        const tableRef = this.$refs[`dataTable${i}`]

        if (tableRef && tableRef.length) {
          const row = tableRef[0].$el.querySelector('.highlighted-row')

          if (row) {
            const topPos = row.offsetTop
            const tableBody = tableRef[0].$el.querySelector('.ivu-table-body')

            tableBody.scrollTop = topPos
          }
        }
      }
    },

    /**
     * Sends a table name as a "Filter Text" for "Golden Query". This is in order to filter the Golden
     * Queries by that specific table.
     * @param {String} tableName
     */
    findInGoldenQuery (tableName) {
      this.$router.push({
        name: 'Golden Query',
        params: {
          initSearchValue: tableName
        }
      })
    },

    /**
     * Validates if a column is a "Foreign Key".
     * @param {Object} key
     * @return {Boolean}
     */
    isForeignKey (key) {
      const { name } = key

      return name && name.toLowerCase() === 'foreign_key'
    },

    /**
     * Filter method used for "AutoComplete" component.
     * @param {String} value
     * @param {String} option
     * @return {String}
     */
    autoCompleteFilterMethod (value, option) {
      return option.toUpperCase().indexOf(value.toUpperCase()) !== -1
    },

    /**
     * Gets the foreign key columns of the table.
     * @param {Object} table - The table whose foreign key columns we want.
     * @return {Array} - Array of foreign key columns.
     */
    getForeignKeyColumns (table) {
      return table.columns.filter(column => column.foreignKey !== null)
    },

    /**
     * Defines the Key property for a column: PRIMARY / FOREIGN / NONE.
     * @param {Object} column - Column Object
     * @param {Object} key - (Optional) a key object obtained from annotations
     * @return {Object} - Object representing the key.
     */
    createKeyObject (column, key = null) {
      if (column.is_primary_raw) {
        return {
          name: 'PRIMARY_KEY'
        }
      } else if (key) {
        let keyName = ''

        if (key.primary) {
          keyName = 'PRIMARY_KEY'
        } else if (key.foreign) {
          keyName = 'FOREIGN_KEY'
        }

        return {
          name: keyName,
          reference: key.reference
        }
      } else if (column.foreignKey) {
        return {
          name: 'FOREIGN_KEY',
          reference: column.foreignKey.sourceTable
        }
      }

      return null
    },

    /**
     * Formats a number using thousand separator.
     * @param {Number} value
     * @param {Number} decimals
     * @return {String}
     */
    formatNumber (value, decimals = 0) {
      let val = Number.parseInt(value).toFixed(decimals)
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    /**
     * Copies the share link into the clipboard.
     * @param {Object} tableData
     * @param {String} scope "schema" or "table".
     */
    shareLink (tableData) {
      const linkToShare = `${window.location.origin}/data-catalog/${tableData.database.toLowerCase()}/${tableData.schema.toLowerCase()}/${tableData.name.toLowerCase()}`
      this.$copyText(linkToShare).then(
        this.onCopySuccess.bind(this),
        this.onCopyError.bind(this)
      )
    },

    /**
     * Shows the Dag Mapping Modal
     * @param {Object} table - Table object
     */
    async showDagMapping (table) {
      this.$set(this.dagMappingModal, 'table', table)
      this.$set(this.dagMappingModal, 'visible', true)
      this.$set(this.dagMappingModal, 'loading', true)

      try {
        const dagFiles = await axios.get(
          `${process.env.VUE_APP_API_URI}/data-catalog/dag-mapping/${table.database}/${table.schema}/${table.name}`
        )
        this.$set(this.dagMappingModal, 'dagFiles', dagFiles.data)

        if (dagFiles.data.length > 0) {
          this.$set(this.dagMappingModal, 'selectedDagFilePath', dagFiles.data[0].path)
        }

        this.$set(this.dagMappingModal, 'loading', false)
      } catch (error) {
        console.error(`Error while retrieving DAG files: ${error}`)
        this.$set(this.dagMappingModal, 'visible', false)
        this.$Message.error({
          content: `Error while retrieving DAG code for ${this.getFullTableName(table)}`,
          duration: 5
        })
      }
    },

    /**
     * Retrieves the DAG Code Mirror object
     * @return {Object} - The Code Mirror object
     */
    getDagCodeMirror () {
      return this.$refs['dag-cm'].cminstance
    },

    /**
     * Highlights all instances of the table name within Code Mirror. The table being referred
     * to is the same table whose DAG files we are viewing
     */
    highlightTableNameInDagFile () {
      // Clear all marked text
      this.markedTexts.forEach(markedText => {
        markedText.clear()
      })
      this.markedTexts = []

      const tableName = this.dagMappingModal.table ? this.dagMappingModal.table.name : ''

      if (!tableName) {
        return
      }

      const dagCodeMirror = this.getDagCodeMirror()
      const cursor = dagCodeMirror.getSearchCursor(tableName, 0, true)

      while (cursor.findNext()) {
        this.markedTexts.push(dagCodeMirror.markText(
          cursor.from(),
          cursor.to(),
          { css: 'background-color:highlight;' }
        ))
      }
    },

    /**
     * Evoked when the Dark Mode switch for DAG code mirror is clicked
     * @param {Boolean} darkMode - True if changed to dark mode. False otherwise
     */
    onDarkModeSwitch (darkMode) {
      const theme = darkMode ? 'mbo' : 'default'
      this.$set(this.dagCmOptions, 'theme', theme)
      this.$set(this.$root.preferences.dataCatalog, 'dagCmDarkMode', darkMode)
    },

    /**
     * Evoked when DAG Mapping Modal is closed
     */
    onDagMappingModalClose () {
      this.$set(this.dagMappingModal, 'visible', false)
      this.$set(this.dagMappingModal, 'table', null)
    },

    /**
     * Displays a success message if the copy to clipboard was successful.
     */
    onCopySuccess () {
      this.$Message.info({
        content: 'Copied to clipboard!',
        duration: 2
      })
    },

    /**
     * Displays an error message if the copy to clipboard failed.
     */
    onCopyError () {
      this.$Message.error({
        content: 'Error while copying to clipboard!',
        duration: 2
      })
    },

    /**
     * Updates the preference about to block hints.
     * @param {Number} hintId
     * @param {Boolean} isHidden
     */
    updateHintPreference (hintId, isHidden) {
      this.hintsPreferences[hintId] = isHidden
      const updatedHint = this.hints.find(hint => hint.id === hintId)
      this.$set(updatedHint, 'hidden', isHidden)
      this.$set(this.$root.preferences.dataCatalog, 'hints', JSON.stringify(this.hintsPreferences))
    },

    /**
     * Filters the results by the filter criteria (All / Table Name / Field Name / Owner).
     * @param {String} filterCriteria
     */
    updateFilterCriteria (filterCriteria) {
      this.filterCriteria = filterCriteria || 'all'
      this.filterResults()
    },

    /**
     * Filters the results by the filter mode (abc / .*).
     * @param {String} filterMode
     */
    updateFilterMode (filterMode) {
      this.filterMode = filterMode || 'abc'
      this.filterResults()
    },

    /**
     * Filters the results by the selected Database.
     * @param {String} selectedDatabase
     */
    updateDatabase (selectedDatabase) {
      this.selectedSchema = ''
      this.databaseIndex = this.databasesList.findIndex(database => database.name === selectedDatabase)
      this.selectedDatabase = selectedDatabase || ''
      this.hasExternalFilters = this.selectedDatabase.length > 0
      this.filterResults()
    },

    /**
     * Filters the results by the selected Schema.
     * @param {String} selectedSchema
     */
    updateSchema (selectedSchema) {
      this.selectedSchema = selectedSchema || ''
      this.hasExternalFilters = this.selectedSchema.length > 0
      this.filterResults()
    },

    /**
     * Gets the full name of the table.
     * @param {Object} table - the table to get full name of
     * @return {String} - full table name
     */
    getFullTableName (table) {
      const { database, schema, name } = table

      return `${database}.${schema}.${name}`
    },

    /**
     * Shows the Table Detail Modal. Evokes when a Foreign Key Link is Clicked
     * @param {String} tableName - The name of the table whose details we are showing
     */
    showTableDetailModal (tableName) {
      const tableNameLowerCase = tableName.toLowerCase()
      const table = this.tableMetadata.tables.find(table => this.getFullTableName(table).toLowerCase() === tableNameLowerCase)

      if (table) {
        this.$set(this.tableDetailModalData, 'table', table)
        this.$set(this.tableDetailModalData, 'visible', true)
      } else {
        this.$Message.error(`Error: Cannot show details of table ${tableName} as it does not exist.`)
      }
    },

    /**
     * Previews the table data by displaying 100 rows from the table
     * @param {Object} table - The table whose rows we are displaying
     */
    async previewTableData (table) {
      const fullTableName = this.getFullTableName(table)
      try {
        this.$set(this.queryTableModalData, 'title', `Table Data Preview for ${fullTableName}`)
        this.$set(this.queryTableModalData, 'loading', true)
        this.$set(this.queryTableModalData, 'visible', true)

        const query = `SELECT * FROM ${fullTableName} LIMIT 100`
        const results = (await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/snowflake/queries`,
          data: {
            query
          }
        })).data

        this.$set(this.queryTableModalData, 'data', results.data)
        this.$set(this.queryTableModalData, 'loading', false)
      } catch (error) {
        const message = `Error while previewing data for ${fullTableName}: ${error}`

        this.$Message.error(message)
      }
    }
  }
}
</script>
<style scoped>
  .col-left {
    width: calc(100% - 496px);
  }
  .col-right {
    width: 488px;
    margin-left: 8px;
  }

  .no-data-tables-msg {
    text-align: center;
    font-size: 32px;
    margin-top: 15%;
    margin-bottom: 15%;
  }
  .search-mode-select,
  .search-filter-select {
    width: 64px;
    font-weight: bold;
    margin: -8px 0;
    text-align: left;
  }
  .search-filter-select {
    width: 104px;
  }
  .search-filter-select >>> .ivu-select-dropdown {
    text-align: left;
  }
  .data-catalog-table >>> .highlighted-row td {
    background-color: #2db7f5;
    color: var(--color-white)
  }
  .action-icon {
    width: 24px;
    margin: 0 2px;
    font-size: 16px;
    cursor: pointer;
    text-align: center;
  }
  .change-log-divider {
    margin: 8px 0;
    font-size: 14px;
  }
  .table-description-container {
    display: flex;
    justify-content: space-between;
  }
  .field-description-container {
    display: flex;
    justify-content: space-between;
  }
  .foreign-ref-autocomplete >>> .ivu-auto-complete.ivu-select-dropdown {
    max-height: 250px;
  }
  .refresh-container {
    margin: 8px 0;
    display: flex;
    justify-content: flex-end;
    right: 0px;
    z-index: 1;
    position: absolute;
  }
  .refresh-icon:hover {
    color: var(--color-blue);
    cursor: pointer;
  }
  .last-updated-text {
    margin-top: 2px;
  }
  .search-by-section >>> .ivu-menu-item:hover {
    color: #515a6e;
    border-color: transparent;
  }

  .search-box,
  .search-box >>> .ivu-input {
    height: 40px;
  }

  .share-links {
    display: flex;
    flex-direction: column;
  }

  .share-links a {
    color: var(--color-white);
    padding: 4px 0;
  }

  .filter .dropdown .elements .row {
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
  }

  .filter .dropdown .elements .row .element-row,
  .filter .dropdown .elements .row .element-row p {
    margin: 0;
    padding: 0 8px;
    width: 100%;
  }

  .filter .dropdown .elements .row .element-row {
    padding: 0;
    display: flex;
    flex-direction: row;
  }

  .filter .dropdown .elements .row .element-row p {
    margin: 0;
  }

  .right {
    display: flex;
    justify-content: flex-end;
  }

  .filter .dropdown .elements .row .select-input {
    width: 100%;
  }

  .filter .dropdown .elements .row .select-input.left >>> .ivu-select-selection {
    width: 100%;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.right >>> .ivu-select-selection {
    width: 100%;
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .filter .dropdown .elements .row .select-input.big-list >>> .ivu-select-dropdown {
    transform-origin: center top !important;
    top: unset !important;
    max-height: 400px;
    overflow-y: scroll;
  }

  .filter .dropdown .elements .row .select-input >>> .ivu-select-not-found {
    color: var(--color-red-alert);
    font-size: 12px;
    list-style: none;
    text-align: left;
    padding: 7px 16px;
  }

  .dag-codemirror >>> .CodeMirror {
    border: 1px solid;
  }
</style>
